import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Map as MapIcon } from "lucide-react";
import type { PageIssue, SitemapErrorDetails, IndexabilityFix } from "@/types/technicalAudit";
import {
	classifySitemapCause,
	SITEMAP_CAUSE_META,
	type SitemapCause,
} from "./sitemapCause";

interface SitemapErrorCardProps {
	issue: PageIssue;
}

// B2: surface WHY a sitemap URL is flagged (dead / redirect / noindex in sitemap)
// and the resulting action, so an SEO reviewer can evaluate it before approving.
// Renders above the ResolverSuggestionCard (which handles the editable suggestion).
export function SitemapErrorCard({ issue }: SitemapErrorCardProps) {
	const details = (issue.issue_details || {}) as SitemapErrorDetails;
	const proposedFix = issue.proposed_fix as
		| (IndexabilityFix & { sitemap_cause?: string })
		| null;

	const cause: SitemapCause =
		(proposedFix?.sitemap_cause as SitemapCause) ||
		classifySitemapCause(details.reason, issue.status_code);
	const meta = SITEMAP_CAUSE_META[cause] ?? SITEMAP_CAUSE_META.other;

	return (
		<Card className="border-border shadow-sm">
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
					<div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center">
						<MapIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
					</div>
					Sitemap Issue
				</CardTitle>
			</CardHeader>
			<CardContent className="pt-0 space-y-4">
				<div className="text-sm text-foreground break-all">{issue.page_url}</div>

				<div className="space-y-1.5">
					<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
						Root cause
					</div>
					<div className="p-3 bg-muted rounded-lg border border-border space-y-2">
						<div className="flex flex-wrap items-center gap-2">
							<Badge variant="outline" className="bg-muted text-foreground border-border">
								{meta.label}
							</Badge>
							<Badge
								variant="outline"
								className={
									meta.autoFixable
										? "bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800"
										: "bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800"
								}
							>
								{meta.autoFixable
									? `Auto-fix: ${meta.actionLabel}`
									: meta.actionLabel}
							</Badge>
						</div>
						<p className="text-sm text-foreground">
							{details.reason ? `${details.reason}. ` : ""}
							{meta.explanation}
						</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
